import { autoinject } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';
import { AsyncErrorHandler } from 'lib/ui';
import { WeldingMachineDto } from 'models/pen/WeldingMachine';
import { DeleteDialogService } from 'services/delete-dialog-service';
import { ErrorService } from 'services/error-service';
import { WeldingMachineService } from 'services/pen/welding-machine-service';
import { ToastService } from 'services/toast-service';

@autoinject
export class WeldingMachineContainer {
  protected headers: {
    title: string;
  }[] = [];
  protected weldingMachines: WeldingMachineDto[] = [];

  constructor(
    private errorService: ErrorService,
    private toast: ToastService,
    private weldingMachineService: WeldingMachineService,
    private t: I18N,
    private deleteDialogService: DeleteDialogService
  ) {}

  protected async attached() {
    this.createHeaders();
    await this.getWeldingMachines();
  }

  @AsyncErrorHandler
  private async getWeldingMachines() {
    this.weldingMachines = await this.weldingMachineService.getAll();
  }

  private async onDelete(id: number, weldingMachineDto: WeldingMachineDto) {
    const inUse = await this.checkInUse(id);
    if (inUse) {
      this.deleteDialogService.confirmBeforeDelete(
        () => {
          weldingMachineDto.Active = false;
          this.weldingMachineService
            .put(weldingMachineDto, id)
            .then((res) => {
              this.toast.showSuccess(this.t.tr('general.weldingMachineInactive'));
              void this.getWeldingMachines();
            })
            .catch((err) => this.errorService.handleError(err));
        },
        'dialog.deleteHeading',
        'general.weldingMachineWarning',
        'general.yes',
        'general.no'
      );
    } else {
      await this.weldingMachineService.delete(id);
      this.toast.showSuccess(this.t.tr('general.deleted'));
      await this.getWeldingMachines();
    }
  }

  @AsyncErrorHandler
  private async onSave(weldingMachineDto: WeldingMachineDto) {
    if (weldingMachineDto.Id) {
      await this.weldingMachineService.put(weldingMachineDto, weldingMachineDto.Id);
    } else {
      await this.weldingMachineService.post(weldingMachineDto);
    }
    this.toast.showSuccess(this.t.tr('general.saved'));
    await this.getWeldingMachines();
  }

  @AsyncErrorHandler
  private async checkInUse(id: number) {
    const { InUse } = await this.weldingMachineService.checkInUse(id);
    return InUse;
  }

  protected isCreating = false;
  protected editingWeldingMachine: number | undefined = undefined;
  protected setEditingWeldingMachine(id: number) {
    this.isCreating = false;
    this.editingWeldingMachine = id;
  }

  protected setIsCreatingWeldingMachine(isCreating: boolean) {
    this.editingWeldingMachine = undefined;
    this.isCreating = isCreating;
  }

  private createHeaders() {
    this.headers = [
      { title: this.t.tr('general.producer') },
      { title: this.t.tr('general.machineNumber') },
      { title: this.t.tr('general.model') },
      { title: this.t.tr('general.calibrationDate') },
      { title: this.t.tr('general.active') },
      { title: this.t.tr('general.actions') },
    ];
  }
}
